import { type ReactNode, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { buscarVouchers } from '../lib/api';
import { valorConfig } from '../lib/formato';
import { useConfiguracaoPublica, useMe } from '../lib/hooks';
import { marcarVouchersVistos, vouchersVistos } from '../lib/armazenamento';
import { PortalShell } from '../componentes/PortalShell';
import { CardVoucher } from '../componentes/CardVoucher';
import { Esqueleto, EstadoVazioIndicar, FalhaRede } from '../componentes/blocos';

/**
 * Meus vouchers (UX §5.4) — lista, um canhoto por voucher. A API já ordena:
 * disponíveis primeiro (mais recente no topo), retirados depois. O voucher não
 * expira: nunca prazo, contagem ou "última chance".
 */
export function MeusVouchers(): ReactNode {
  const me = useMe();
  const config = useConfiguracaoPublica();
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['me', 'vouchers'],
    queryFn: buscarVouchers,
  });

  const itens = data?.itens ?? [];
  const disponiveis = useMemo(() => itens.filter((v) => v.status === 'disponivel'), [itens]);

  const idsNovos = useMemo(() => {
    const vistos = new Set(vouchersVistos());
    return new Set(disponiveis.filter((v) => !vistos.has(v.id)).map((v) => v.id));
  }, [disponiveis]);

  useEffect(() => {
    if (disponiveis.length) marcarVouchersVistos(disponiveis.map((v) => v.id));
  }, [disponiveis]);

  const brindeIndicado = config.data ? valorConfig(config.data, 'brinde_indicado_descricao') : null;

  return (
    <PortalShell voltarPara="/">
      <h1 className="tipo-titulo text-tinta-forte">Seus brindes</h1>

      <div className="mt-5 flex flex-col gap-4">
        {isLoading && (
          <>
            <Esqueleto className="h-48 rounded-2" />
            <Esqueleto className="h-24 rounded-2" />
          </>
        )}
        {isError && <FalhaRede aoTentarDeNovo={() => void refetch()} />}

        {data && itens.length === 0 && me.data?.codigo && (
          <EstadoVazioIndicar
            codigo={me.data.codigo}
            brindeIndicado={brindeIndicado}
            titulo="Seus brindes vão aparecer aqui quando alguém fizer um serviço com o seu código."
          />
        )}

        {itens.map((v) => (
          <CardVoucher key={v.id} voucher={v} novo={idsNovos.has(v.id)} />
        ))}
      </div>
    </PortalShell>
  );
}
